<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :loading="loading"
    :title="$i18n.t('sidebar.waterBalanceZone.equations')"
    agree-text="dialog.agreeSave"
    no-paddings-y
    no-paddings-x
    header-shadow
    @agree="save"
    @modalClosed="onClose"
    dialog-width="60%"
    min-body-height="300px"
  >
    <template #body>
      <v-row no-gutters v-if="tableDataLoading">
        <v-col cols="12">
          <main-loader />
        </v-col>
      </v-row>
      <v-row no-gutters v-else>
        <v-col cols="12">
          <v-data-table hide-default-footer :headers="tableHeaders" :items="tableData" disable-pagination>
            <template #item.inflow_value="{item}">
              <v-row no-gutters align="center">
                <v-col cols="12" class="py-3">
                  <data-input v-bind="flowValueInput" v-model="item['inflow_value']" />
                </v-col>
              </v-row>
            </template>
            <template #item.outflow_value="{item}">
              <v-row no-gutters align="center">
                <v-col cols="12" class="py-3">
                  <data-input v-bind="flowValueInput" v-model="item['outflow_value']" />
                </v-col>
              </v-row>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </template>
  </main-large-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import MainLargeDialog from '@/components/MainLargeDialog';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'BalanceEquationsDialog',
  components: { DataInput, MainLargeDialog, MainLoader },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    dmaZonesLayerId: {
      type: Number,
      required: true,
    },
    dmaZonesLayerAttributeName: {
      type: String,
      required: true,
    },
    dmaInflowAttribute: {
      type: String,
      required: true,
    },
    dmaOutflowAttribute: {
      type: String,
      required: true,
    },
    flowMeterLayerId: {
      type: Number,
      required: true,
    },
    flowMeterLayerAttributeName: {
      type: String,
      required: true,
    },
  },
  computed: {
    layers: get('layers/layers'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    dmaZonesLayer() {
      return this.layers[this.dmaZonesLayerId];
    },
    dmaZonesLayerAttributes() {
      return this.$_flattenStructure(this.dmaZonesLayer?.form_schema.elements || []);
    },
    dmaZonesLayerAttributeNameLabel() {
      this.$forceUpdate();
      return (
        this.dmaZonesLayerAttributes.find(attribute => attribute.attribute === this.dmaZonesLayerAttributeName)
          ?.label || this.$i18n.t('sidebar.waterBalanceZone.balanceEquations.zoneName')
      );
    },
    flowMeterLayer() {
      return this.layers[this.flowMeterLayerId];
    },
    flowValueInput() {
      return {
        dataType: { name: 'select' },
        items: this.allowedValues,
        multiple: true,
        autoConvertSelect: false,
        chips: true,
        hideDetails: true,
        readOnly: this.allowedValuesLoading,
        loading: this.allowedValuesLoading,
      };
    },
    tableHeaders() {
      return [
        {
          text: this.dmaZonesLayerAttributeNameLabel,
          value: 'descAttributeValue',
          sortable: true,
          align: 'center',
          width: '30%',
        },
        {
          text: this.$i18n.t('sidebar.waterBalanceZone.balanceEquations.attributePlus'),
          value: 'inflow_value',
          sortable: false,
          align: 'center',
          width: '35%',
        },
        {
          text: this.$i18n.t('sidebar.waterBalanceZone.balanceEquations.attributeMinus'),
          value: 'outflow_value',
          sortable: false,
          align: 'center',
          width: '35%',
        },
      ];
    },
  },
  data: () => ({
    allowedValues: [],
    allowedValuesLoading: false,
    loading: false,
    tableDataLoading: false,
    tableData: [],
  }),
  methods: {
    balanceFlowMeters: call('waterBalance/balanceFlowMeters'),
    getDataSourceAttributeUniqueValues: call('layers/getDataSourceAttributeUniqueValues'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    async getAllowedValues() {
      this.allowedValuesLoading = true;
      try {
        const uniqueValues = await this.getDataSourceAttributeUniqueValues({
          dataSourceName: this.flowMeterLayer.data_source_name,
          attributeName: this.flowMeterLayerAttributeName,
          params: {
            features_filter: this.flowMeterLayer.filter_expression,
          },
        });
        this.allowedValues = Object.keys(uniqueValues.values);
        this.allowedValuesLoading = false;
      } catch {
        this.allowedValuesLoading = true;
      }
    },
    async getTableData() {
      this.tableDataLoading = true;
      const payload = {
        layer_id: this.dmaZonesLayerId,
        params: {
          with_features: true,
          with_collection_bbox: false,
          with_count: false,
          with_geometry: false,
        },
      };
      try {
        const r = await this.getLayerFeatures(payload);
        const features = r.data.data.features.features;
        this.tableData = features.map(feature => {
          const inflowValue = feature.properties?.[this.dmaInflowAttribute] || '';
          const outflowValue = feature.properties?.[this.dmaOutflowAttribute] || '';
          return {
            id: feature.id,
            descAttributeValue: feature.properties[this.dmaZonesLayerAttributeName] || feature.id,
            inflow_value: inflowValue ? inflowValue.split(',') : [],
            outflow_value: outflowValue ? outflowValue.split(',') : [],
          };
        });
        this.tableDataLoading = false;
      } catch {
        this.tableDataLoading = true;
      }
    },
    onClose() {
      this.tableData = this.tableData.map(data => {
        return { ...data, inflow_value: [], outflow_value: [] };
      });
    },
    async save() {
      this.loading = true;
      const tableData = this.tableData.map(data => {
        const d = JSON.parse(JSON.stringify(data));
        delete d.descAttributeValue;
        return d;
      });
      const payload = {
        data: tableData,
        inflow_attribute: this.dmaInflowAttribute,
        outflow_attribute: this.dmaOutflowAttribute,
      };
      try {
        await this.balanceFlowMeters({ layerId: this.dmaZonesLayerId, payload });
        this.computedIsVisible = false;
        this.loading = false;
      } catch {
        this.loading = false;
      }
    },
  },
  watch: {
    computedIsVisible: {
      handler(nV) {
        if (nV && this.dmaZonesLayerId && this.dmaZonesLayerAttributeName) {
          this.getTableData();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.$watch(
      vm => {
        const { flowMeterLayerId, flowMeterLayerAttributeName } = vm;
        return { flowMeterLayerId, flowMeterLayerAttributeName };
      },
      nV => {
        const { flowMeterLayerId, flowMeterLayerAttributeName } = nV;
        if (flowMeterLayerId && flowMeterLayerAttributeName) {
          this.getAllowedValues();
        }
      },
      {
        immediate: true,
      }
    );
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .v-select__selections {
    flex-wrap: wrap !important;
  }
  .v-chip--select {
    margin-bottom: 1px !important;
    margin-top: 1px !important;
  }
}
</style>
